import {
  FieldAdornmentOption,
  type FieldMap,
  StaticDataSource,
} from '@snap-types/models/auto-configurator/state';

import { buildLineItemField } from '../builders/line-item';
import { requiredWhenSameRowValuePresent } from '../validation/custom-validator-helpers';

export function createLineItemFields(): FieldMap['lineItem'] {
  return {
    lineNumber: buildLineItemField('lineNumber', {
      type: 'number',
      isReadonly: true,
    }),

    notes: buildLineItemField('notes', { type: 'text', isReadonly: false }),

    toolingNote: buildLineItemField('toolingNote', {
      type: 'text',
      isReadonly: false,
    }),

    headingNote: buildLineItemField('headingNote', {
      type: 'text',
      isReadonly: false,
    }),

    trimmingNote: buildLineItemField('trimmingNote', {
      type: 'text',
      isReadonly: false,
    }),

    pinchNote: buildLineItemField('pinchNote', {
      type: 'text',
      isReadonly: false,
    }),

    rollingNote: buildLineItemField('rollingNote', {
      type: 'text',
      isReadonly: false,
    }),

    shankSlottingNote: buildLineItemField('shankSlottingNote', {
      type: 'text',
      isReadonly: false,
    }),

    sawSlottingNote: buildLineItemField('sawSlottingNote', {
      type: 'text',
      isReadonly: false,
    }),

    dephosNote: buildLineItemField('dephosNote', {
      type: 'text',
      isReadonly: false,
    }),

    heatTreatNote: buildLineItemField('heatTreatNote', {
      type: 'text',
      isReadonly: false,
    }),

    surfaceFinishNote: buildLineItemField('surfaceFinishNote', {
      type: 'text',
      isReadonly: false,
    }),

    postFinishNote: buildLineItemField('postFinishNote', {
      type: 'text',
      isReadonly: false,
    }),

    osp2Note: buildLineItemField('osp2Note', {
      type: 'text',
      isReadonly: false,
    }),

    osp3Note: buildLineItemField('osp3Note', {
      type: 'text',
      isReadonly: false,
    }),

    description: buildLineItemField('description', {
      type: 'description',
      isReadonly: false,
      validation: {
        maxLength: 512,
      },
    }),

    descriptionIsOverridden: buildLineItemField('descriptionIsOverridden', {
      type: 'boolean',
      isReadonly: false,
    }),

    customerPartId: buildLineItemField('customerPartId', {
      type: 'text',
      isReadonly: false,
    }),

    material: buildLineItemField('material', {
      type: 'select',
      isReadonly: false,
      isRequired: true,
      staticDataSource: StaticDataSource.materialGrade,
    }),

    headStyle: buildLineItemField('headStyle', {
      type: 'select',
      isReadonly: false,
      isRequired: true,
      staticDataSource: StaticDataSource.headStyle,
    }),

    dfars: buildLineItemField('dfars', {
      type: 'boolean',
      isReadonly: false,
    }),

    recess: buildLineItemField('recess', {
      type: 'select',
      isReadonly: false,
      isRequired: true,
      staticDataSource: StaticDataSource.driveRecessStyle,
    }),

    serration: buildLineItemField('serration', {
      type: 'boolean',
      isReadonly: false,
    }),

    postFinish: buildLineItemField('postFinish', {
      type: 'select',
      isReadonly: false,
      isRequired: true,
      staticDataSource: StaticDataSource.postFinish,
    }),

    drawingId: buildLineItemField('drawingId', {
      type: 'text',
      isReadonly: false,
    }),

    drawingRevisionNo: buildLineItemField('drawingRevisionNo', {
      type: 'text',
      isReadonly: false,
    }),

    fastenerType: buildLineItemField('fastenerType', {
      type: 'select',
      isReadonly: false,
      isRequired: true,
      staticDataSource: StaticDataSource.fastenerType,
    }),

    threadSize: buildLineItemField('threadSize', {
      type: 'select',
      isReadonly: false,
      isRequired: true,
      staticDataSource: StaticDataSource.threadSize,
    }),

    threadPitch: buildLineItemField('threadPitch', {
      type: 'select',
      isReadonly: false,
      isRequired: true,
      staticDataSource: StaticDataSource.threadPitch,
    }),

    length: buildLineItemField('length', {
      type: 'decimal',
      isReadonly: false,
      isRequired: true,
      rightAdornment: FieldAdornmentOption.dimensionUnit,
      validation: {
        min: 0,
      },
    }),

    shoulderDiameter: buildLineItemField('shoulderDiameter', {
      type: 'decimal',
      isReadonly: false,
      isRequired: false,
      rightAdornment: FieldAdornmentOption.dimensionUnit,
    }),

    shoulderLength: buildLineItemField('shoulderLength', {
      type: 'decimal',
      isReadonly: false,
      isRequired: false,
      rightAdornment: FieldAdornmentOption.dimensionUnit,
      validators: [],
    }),

    surfaceFinish: buildLineItemField('surfaceFinish', {
      type: 'select',
      isReadonly: false,
      isRequired: true,
      staticDataSource: StaticDataSource.surfaceFinish,
    }),

    hasSemsEmbed: buildLineItemField('hasSemsEmbed', {
      type: 'boolean',
      isReadonly: false,
    }),

    sems1Style: buildLineItemField('sems1Style', {
      type: 'select',
      isReadonly: false,
      staticDataSource: StaticDataSource.sems,
      validators: [requiredWhenSameRowValuePresent('lineItem', 'hasSemsEmbed')],
    }),

    sems1Weight: buildLineItemField('sems1Weight', {
      type: 'decimal',
      isReadonly: false,
      validators: [requiredWhenSameRowValuePresent('lineItem', 'sems1Style')],
    }),

    sems2Style: buildLineItemField('sems2Style', {
      type: 'select',
      isReadonly: false,
      staticDataSource: StaticDataSource.sems,
      validators: [requiredWhenSameRowValuePresent('lineItem', 'hasSemsEmbed')],
    }),

    sems2Weight: buildLineItemField('sems2Weight', {
      type: 'decimal',
      isReadonly: false,
      validators: [requiredWhenSameRowValuePresent('lineItem', 'sems2Style')],
    }),

    osp2: buildLineItemField('osp2', {
      type: 'select',
      isReadonly: false,
      staticDataSource: StaticDataSource.osps,
    }),

    osp2Description: buildLineItemField('osp2Description', {
      type: 'text',
      isReadonly: false,
    }),

    osp2UOM: buildLineItemField('osp2UOM', {
      type: 'select',
      staticDataSource: StaticDataSource.unitOfMeasure,
      isReadonly: false,
      validators: [requiredWhenSameRowValuePresent('lineItem', 'osp2')],
    }),

    osp2MinLot: buildLineItemField('osp2MinLot', {
      type: 'decimal',
      isReadonly: false,
      validators: [
        requiredWhenSameRowValuePresent('lineItem', 'osp2Description'),
      ],
    }),

    osp3: buildLineItemField('osp3', {
      type: 'select',
      isReadonly: false,
      staticDataSource: StaticDataSource.osps,
    }),

    osp3Description: buildLineItemField('osp3Description', {
      type: 'text',
      isReadonly: false,
    }),

    osp3UOM: buildLineItemField('osp3UOM', {
      type: 'select',
      staticDataSource: StaticDataSource.unitOfMeasure,
      isReadonly: false,
      validators: [requiredWhenSameRowValuePresent('lineItem', 'osp3')],
    }),

    osp3MinLot: buildLineItemField('osp3MinLot', {
      type: 'decimal',
      isReadonly: false,
      validators: [
        requiredWhenSameRowValuePresent('lineItem', 'osp3Description'),
      ],
    }),
  };
}

export const lineItemFields = createLineItemFields();
